import { FIRST_INDEX } from './fractionalIndex.js';
import { createNodeId, nanoid } from './id.js';
import { STICKY_COLORS, contrastText } from './palette.js';
import type {
  BaseNode,
  ConnectorEndpoint,
  ConnectorNode,
  DrawNode,
  DrawPoint,
  EmbedNode,
  FrameNode,
  GroupNode,
  ImageNode,
  NodeId,
  NotebookNode,
  NotebookPage,
  ShapeKind,
  ShapeNode,
  ShapeStyle,
  StickyNode,
  TextNode,
  TextStyle,
  UserId,
  Vec,
} from './types.js';

export const DEFAULT_TEXT_STYLE: TextStyle = {
  color: '#1A1F28',
  fontSize: 16,
  fontFamily: 'sans',
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  align: 'center',
  valign: 'middle',
  lineHeight: 1.32,
};

export const DEFAULT_SHAPE_STYLE: ShapeStyle = {
  fill: '#CBE2FF',
  fillStyle: 'solid',
  stroke: '#1C7ED6',
  strokeWidth: 2,
  strokeStyle: 'solid',
  radius: 8,
  shadow: false,
};

export const STICKY_SIZE = 180;
export const DEFAULT_SHAPE_SIZE = { w: 200, h: 140 };
export const DEFAULT_FRAME_SIZE = { w: 1600, h: 900 };
export const DEFAULT_NOTEBOOK_SIZE = { w: 280, h: 360 };
/**
 * Fixed inner page size in CSS pixels — independent of the cover on the board.
 *
 * Wider than a sheet of paper on purpose: the workspace fits the page by
 * height on a landscape screen, so width is the only dimension that buys
 * more room to write. Strokes and images are stored in page coordinates from
 * the top-left corner, so widening the page keeps existing content in place
 * and simply adds space to the right.
 */
export const NOTEBOOK_PAGE_SIZE = { w: 1092, h: 1034 };
export const NOTEBOOK_COVER_COLORS = [
  '#E8F1FF',
  '#FFF4D6',
  '#E6F6EA',
  '#FCE8F0',
  '#F0E8FF',
  '#FFE8E0',
] as const;

export function createNotebookPage(overrides: Partial<NotebookPage> = {}): NotebookPage {
  return {
    id: `pg_${nanoid(10)}`,
    text: '',
    strokes: [],
    images: [],
    ...overrides,
  };
}

/** Resolve page style, including boards saved before `paper` existed. */
export function notebookPaper(node: {
  paper?: NotebookNode['paper'];
  ruled?: boolean;
}): NotebookNode['paper'] {
  if (node.paper) return node.paper;
  if (node.ruled === false) return 'blank';
  return 'lined';
}

export const NOTEBOOK_PAPER_LABELS: Record<NotebookNode['paper'], string> = {
  lined: 'В линейку',
  grid: 'В клетку',
  blank: 'Без линий',
};

/** Named frame presets, matching the aspect ratios people actually present in. */
export const FRAME_PRESETS = [
  { name: '16:9', w: 1600, h: 900 },
  { name: '4:3', w: 1440, h: 1080 },
  { name: '1:1', w: 1080, h: 1080 },
  { name: 'A4 альбомная', w: 1123, h: 794 },
  { name: 'A4 книжная', w: 794, h: 1123 },
  { name: 'Телефон', w: 390, h: 844 },
  { name: 'Компьютер', w: 1440, h: 900 },
] as const;

export interface NodeFactoryContext {
  userId: UserId;
  index?: string;
  parentId?: NodeId | null;
}

function base(
  type: BaseNode['type'],
  x: number,
  y: number,
  w: number,
  h: number,
  ctx: NodeFactoryContext,
): BaseNode {
  const now = Date.now();
  return {
    id: createNodeId(),
    type,
    x,
    y,
    w,
    h,
    rotation: 0,
    index: ctx.index ?? FIRST_INDEX,
    parentId: ctx.parentId ?? null,
    opacity: 1,
    locked: false,
    createdBy: ctx.userId,
    createdAt: now,
    updatedAt: now,
    link: null,
  };
}

export function createShape(
  kind: ShapeKind,
  x: number,
  y: number,
  ctx: NodeFactoryContext,
  overrides: Partial<ShapeNode> = {},
): ShapeNode {
  const w = overrides.w ?? DEFAULT_SHAPE_SIZE.w;
  const h = overrides.h ?? DEFAULT_SHAPE_SIZE.h;
  const style = { ...DEFAULT_SHAPE_STYLE, ...overrides.style };
  return {
    ...base('shape', x, y, w, h, ctx),
    type: 'shape',
    shape: kind,
    style,
    text: '',
    textStyle: {
      ...DEFAULT_TEXT_STYLE,
      color: style.fill === 'transparent' ? DEFAULT_TEXT_STYLE.color : contrastText(style.fill),
      ...overrides.textStyle,
    },
    ...overrides,
  } as ShapeNode;
}

export function createSticky(
  x: number,
  y: number,
  ctx: NodeFactoryContext,
  overrides: Partial<StickyNode> = {},
): StickyNode {
  const fill = overrides.fill ?? STICKY_COLORS[0].value;
  return {
    ...base('sticky', x, y, overrides.w ?? STICKY_SIZE, overrides.h ?? STICKY_SIZE, ctx),
    type: 'sticky',
    fill,
    text: '',
    autoGrow: true,
    textStyle: {
      ...DEFAULT_TEXT_STYLE,
      fontSize: 18,
      color: contrastText(fill),
      ...overrides.textStyle,
    },
    ...overrides,
  } as StickyNode;
}

export function createText(
  x: number,
  y: number,
  ctx: NodeFactoryContext,
  overrides: Partial<TextNode> = {},
): TextNode {
  return {
    ...base('text', x, y, overrides.w ?? 240, overrides.h ?? 32, ctx),
    type: 'text',
    text: '',
    autoHeight: true,
    background: null,
    textStyle: {
      ...DEFAULT_TEXT_STYLE,
      fontSize: 24,
      align: 'left',
      valign: 'top',
      ...overrides.textStyle,
    },
    ...overrides,
  } as TextNode;
}

export function createDraw(
  points: DrawPoint[],
  ctx: NodeFactoryContext,
  overrides: Partial<DrawNode> = {},
): DrawNode {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  if (!Number.isFinite(minX)) {
    minX = 0;
    minY = 0;
    maxX = 0;
    maxY = 0;
  }
  const size = overrides.size ?? 6;
  return {
    ...base('draw', minX, minY, Math.max(maxX - minX, 1), Math.max(maxY - minY, 1), ctx),
    type: 'draw',
    points,
    color: '#1A1F28',
    size,
    brush: 'pen',
    closed: false,
    ...overrides,
  } as DrawNode;
}

export function createConnector(
  start: ConnectorEndpoint,
  end: ConnectorEndpoint,
  ctx: NodeFactoryContext,
  overrides: Partial<ConnectorNode> = {},
): ConnectorNode {
  return {
    ...base('connector', 0, 0, 0, 0, ctx),
    type: 'connector',
    start,
    end,
    routing: 'elbow',
    startHead: 'none',
    endHead: 'filledTriangle',
    stroke: '#1A1F28',
    strokeWidth: 2,
    strokeStyle: 'solid',
    text: '',
    textStyle: { ...DEFAULT_TEXT_STYLE, fontSize: 13, valign: 'middle' },
    bend: [],
    ...overrides,
  } as ConnectorNode;
}

export function createFrame(
  x: number,
  y: number,
  ctx: NodeFactoryContext,
  overrides: Partial<FrameNode> = {},
): FrameNode {
  return {
    ...base(
      'frame',
      x,
      y,
      overrides.w ?? DEFAULT_FRAME_SIZE.w,
      overrides.h ?? DEFAULT_FRAME_SIZE.h,
      ctx,
    ),
    type: 'frame',
    name: 'Фрейм',
    fill: '#FFFFFF',
    clip: true,
    slideIndex: null,
    ...overrides,
  } as FrameNode;
}

export function createImage(
  x: number,
  y: number,
  src: string,
  naturalW: number,
  naturalH: number,
  ctx: NodeFactoryContext,
  overrides: Partial<ImageNode> = {},
): ImageNode {
  // Cap the initial size so dropping a 6000px photo does not fill the viewport.
  const maxEdge = 640;
  const scale = Math.min(1, maxEdge / Math.max(naturalW, naturalH, 1));
  return {
    ...base('image', x, y, naturalW * scale, naturalH * scale, ctx),
    type: 'image',
    src,
    naturalW,
    naturalH,
    radius: 0,
    crop: null,
    mask: null,
    alt: '',
    ...overrides,
  } as ImageNode;
}

export function createGroup(
  children: NodeId[],
  bounds: { x: number; y: number; w: number; h: number },
  ctx: NodeFactoryContext,
): GroupNode {
  return {
    ...base('group', bounds.x, bounds.y, bounds.w, bounds.h, ctx),
    type: 'group',
    children,
  } as GroupNode;
}

export function createEmbed(
  x: number,
  y: number,
  url: string,
  ctx: NodeFactoryContext,
  overrides: Partial<EmbedNode> = {},
): EmbedNode {
  return {
    ...base('embed', x, y, overrides.w ?? 640, overrides.h ?? 360, ctx),
    type: 'embed',
    url,
    provider: detectEmbedProvider(url),
    title: '',
    ...overrides,
  } as EmbedNode;
}

export function createNotebook(
  x: number,
  y: number,
  ctx: NodeFactoryContext,
  overrides: Partial<NotebookNode> = {},
): NotebookNode {
  const pages = overrides.pages?.length
    ? overrides.pages
    : [createNotebookPage(), createNotebookPage(), createNotebookPage()];
  return {
    ...base(
      'notebook',
      x,
      y,
      DEFAULT_NOTEBOOK_SIZE.w,
      DEFAULT_NOTEBOOK_SIZE.h,
      ctx,
    ),
    type: 'notebook',
    title: 'Тетрадь',
    pageIndex: 0,
    coverColor: NOTEBOOK_COVER_COLORS[0],
    paper: 'grid',
    ...overrides,
    // Cover size is always fixed — drag-create / overrides must not change it.
    w: DEFAULT_NOTEBOOK_SIZE.w,
    h: DEFAULT_NOTEBOOK_SIZE.h,
    pages,
  } as NotebookNode;
}

export function detectEmbedProvider(url: string): EmbedNode['provider'] {
  if (/youtube\.com|youtu\.be/i.test(url)) return 'youtube';
  if (/vimeo\.com/i.test(url)) return 'vimeo';
  if (/figma\.com/i.test(url)) return 'figma';
  if (/docs\.google\.com/i.test(url)) return 'gdocs';
  return 'iframe';
}

/** Normalises a share URL into something an iframe will actually render. */
export function embedSrc(node: EmbedNode): string {
  const url = node.url;
  if (node.provider === 'youtube') {
    const id =
      url.match(/[?&]v=([\w-]{6,})/)?.[1] ??
      url.match(/youtu\.be\/([\w-]{6,})/)?.[1] ??
      url.match(/embed\/([\w-]{6,})/)?.[1];
    return id ? `https://www.youtube.com/embed/${id}` : url;
  }
  if (node.provider === 'vimeo') {
    const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
    return id ? `https://player.vimeo.com/video/${id}` : url;
  }
  if (node.provider === 'figma') {
    return `https://www.figma.com/embed?embed_host=kanvas&url=${encodeURIComponent(url)}`;
  }
  return url;
}

/** Offset applied when duplicating, so the copy is visibly on top. */
export const DUPLICATE_OFFSET: Vec = { x: 24, y: 24 };
